/**
 * Lifecycle pinned-scene scroll model.
 *
 * Track = structural scroll spacer (no visible panel).
 * Sticky scene = full viewport frame; stages change inside via transitionDistance only.
 *
 * trackHeightPx = sceneViewportHeight + transitionDistance + releaseBuffer
 */

import { getHealthFixedNavOffsetPx } from "./healthNavOffset";

export const LIFECYCLE_STAGE_COUNT = 5;
export const LIFECYCLE_TRANSITION_COUNT = 4;

/** Sticky top offset (matches top-20 / lg:top-24) */
export const LIFECYCLE_STICKY_TOP_PX = 80;
export const LIFECYCLE_STICKY_TOP_LG_PX = 96;

/** Per transition: clamp(vh × 0.22, 168px, 280px) */
export const LIFECYCLE_STAGE_STEP_VH_RATIO = 0.22;
export const LIFECYCLE_STAGE_STEP_MIN_PX = 168;
export const LIFECYCLE_STAGE_STEP_MAX_PX = 280;

/** After Stage 05 before the pinned scene releases */
export const LIFECYCLE_RELEASE_BUFFER_PX = 24;

export const LIFECYCLE_TIMELINE_AXIS_CLASS = "left-4";

export const LIFECYCLE_DESKTOP_MQ = "(min-width: 1024px)";

export const LIFECYCLE_STICKY_TOP_CLASS = "top-20 lg:top-24";

export interface LifecycleScrollMetrics {
    stickyTopPx: number;
    sceneHeightPx: number;
    stageStepPx: number;
    transitionDistancePx: number;
    releaseBufferPx: number;
    trackHeightPx: number;
}

export function getStickyTopOffsetPx(): number {
    if (typeof window === "undefined") return LIFECYCLE_STICKY_TOP_PX;
    return getHealthFixedNavOffsetPx();
}

/** Viewport height available to the pinned scene below the navbar offset */
export function getLifecycleSceneHeightPx(): number {
    if (typeof window === "undefined") return 600;
    return Math.max(400, window.innerHeight - getStickyTopOffsetPx());
}

export function getLifecycleStageStepPx(): number {
    if (typeof window === "undefined") return LIFECYCLE_STAGE_STEP_MIN_PX;
    const raw = window.innerHeight * LIFECYCLE_STAGE_STEP_VH_RATIO;
    return Math.round(
        Math.min(
            LIFECYCLE_STAGE_STEP_MAX_PX,
            Math.max(LIFECYCLE_STAGE_STEP_MIN_PX, raw)
        )
    );
}

export function getLifecycleTransitionDistancePx(): number {
    return getLifecycleStageStepPx() * LIFECYCLE_TRANSITION_COUNT;
}

/** Track height from viewport scene + transitions (not content-measured inner height) */
export function getLifecycleScrollMetrics(): LifecycleScrollMetrics {
    const stickyTopPx = getStickyTopOffsetPx();
    const sceneHeightPx = getLifecycleSceneHeightPx();
    const stageStepPx = getLifecycleStageStepPx();
    const transitionDistancePx = stageStepPx * LIFECYCLE_TRANSITION_COUNT;
    const releaseBufferPx = LIFECYCLE_RELEASE_BUFFER_PX;
    const trackHeightPx =
        sceneHeightPx + transitionDistancePx + releaseBufferPx;

    return {
        stickyTopPx,
        sceneHeightPx,
        stageStepPx,
        transitionDistancePx,
        releaseBufferPx,
        trackHeightPx: Math.round(trackHeightPx),
    };
}

/**
 * Pixels scrolled through stage transitions after the track pins (0 → transitionDistance).
 */
export function getLifecycleTransitionScrollPx(
    trackElement: HTMLElement,
    stickyTopPx: number,
    transitionDistancePx: number
): number {
    if (transitionDistancePx <= 0) return 0;
    const trackTop = trackElement.getBoundingClientRect().top;
    if (trackTop >= stickyTopPx) return 0;
    return Math.min(transitionDistancePx, stickyTopPx - trackTop);
}

export function getLifecycleStageIndexFromTransitionScroll(
    transitionScrollPx: number,
    transitionDistancePx: number,
    stageCount: number
): number {
    if (stageCount <= 0 || transitionDistancePx <= 0) return 0;

    const t = Math.min(
        1,
        Math.max(0, transitionScrollPx / transitionDistancePx)
    );
    if (t >= 1) return stageCount - 1;

    const index = Math.floor(t * stageCount);
    return Math.min(stageCount - 1, Math.max(0, index));
}

export function getScrollYForLifecycleStage(
    trackElement: HTMLElement,
    metrics: LifecycleScrollMetrics,
    stageIndex: number,
    stageCount: number
): number {
    if (stageCount <= 1 || metrics.transitionDistancePx <= 0) {
        return trackElement.getBoundingClientRect().top + window.scrollY;
    }
    const trackDocTop =
        trackElement.getBoundingClientRect().top + window.scrollY;
    const fraction = stageIndex / (stageCount - 1);
    const targetTransitionPx = fraction * metrics.transitionDistancePx;
    return trackDocTop - metrics.stickyTopPx + targetTransitionPx;
}

export const HERO_LOOP_CONNECTOR_POINTS: { x: number; y: number }[] = [
    { x: 50, y: 8 },
    { x: 88, y: 34 },
    { x: 72, y: 88 },
    { x: 28, y: 88 },
    { x: 12, y: 34 },
];
